import { EventEmitter } from "client_legacy/ndaw/eventEmitter";
import { newGuid, Guid } from "client_legacy/ndaw/guid";

export type StackedStateCleanUp = () => void;
export type StackedStateSetValue<TValue> = (value: TValue) => StackedStateCleanUp;

export class StackedState<TValue> {
	private readonly _stack: StackItem<TValue>[];
	private readonly _eventEmitter: EventEmitter<"TopValueChange">;

	constructor() {
		this._stack = [];
		this._eventEmitter = new EventEmitter<"TopValueChange">();
	}

	public getTopValue(): TValue | undefined {
		const stack: StackItem<TValue>[] = this._stack;

		if (stack.length === 0) {
			return undefined;
		}

		return stack[stack.length - 1].value;
	}

	public push(value: TValue): Guid {
		const newItem: StackItem<TValue> = {
			value,
			handle: newGuid()
		};

		this._stack.push(newItem);
		this.emitTopValueChange();

		return newItem.handle;
	}

	public remove(handle: Guid): TValue | undefined {
		const indexOfItemToRemove: number = this._stack.findIndex(i => i.handle === handle);

		if (indexOfItemToRemove !== -1) {
			const poppedItem: StackItem<TValue> = this._stack
				.splice(indexOfItemToRemove, 1)[0];

			this.emitTopValueChange();

			return poppedItem.value;
		}

		return undefined;
	}

	public onTopValueChange(callback: (value: TValue | undefined) => void): void {
		this._eventEmitter
			.on("TopValueChange", callback);
	}

	public offTopValueChange(callback: (value: TValue | undefined) => void): void {
		this._eventEmitter
			.off("TopValueChange", callback);
	}

	private emitTopValueChange(): void {
		this._eventEmitter.emit("TopValueChange", this.getTopValue());
	}

	public destroy(): void {
		this._eventEmitter
			.destroy();
	}
}

export function createStackedState<TValue>(
	onSetTopValue: (value: TValue | undefined) => void
): StackedStateSetValue<TValue> {
	const stack = new StackedState<TValue>();

	stack.onTopValueChange(onSetTopValue);

	return (value: TValue): StackedStateCleanUp => {
		const handle: Guid = stack.push(value);

		const cleanUp: StackedStateCleanUp = (): void => {
			stack.remove(handle);
		};

		return cleanUp;
	};
}

type StackItem<TValue> = {
	value: TValue;
	handle: Guid;
};